'use client'

import { ArrowUpRight, ChevronRight, Mail, MapPin, Navigation, Phone } from "lucide-react"
import { resolveColorHex, getVendorPrimaryColor } from "@/utils/util"

function DirRow({ label, headline, sub, cta, icon: Icon, href, primary }: { label: string, headline: string, sub?: string, cta: string, icon: any, href: string, primary: string }) {
  return (
    <a href={href} className="group flex items-center gap-4 py-5 border-b border-border" style={{ ['--brand' as any]: primary }}>
      <div className="h-11 w-11 rounded-lg grid place-items-center shrink-0" style={{ background: `color-mix(in oklch, ${primary} 10%, white)`, color: primary }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-base font-medium text-foreground wrap-break-word group-hover:text-(--brand) transition-colors">{headline}</div>
        {sub && <div className="mt-0.5 text-[13px] text-muted-foreground line-clamp-1">{sub}</div>}
      </div>
      <div className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
        {cta}
        <ArrowUpRight className="h-4 w-4" />
      </div>
    </a>
  )
}

export default function Contact({ vendor }: { vendor?: any }) {
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))

  return (
    <div className="bg-white">
      <div className="mx-auto w-full max-w-3xl px-4 lg:px-8 pt-10 sm:pt-8 pb-12">
        <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-8">
          <a href="/" className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">Холбоо барих</span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
          Бидэнтэй холбогдох
        </h1>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-prose">
          Бүтээгдэхүүн, захиалга, хүргэлттэй холбоотой асуултад утас, имэйлээр эсвэл биечлэн дэлгүүрт ирж хариу авах боломжтой.
        </p>

        <div className="mt-8 border-t border-border">
          {vendor?.phone && (
            <DirRow
              label="Утас"
              href={`tel:${vendor.phone}`}
              cta="Залгах"
              icon={Phone}
              primary={primary}
              headline={vendor.phone}
              sub="Худалдагчтай шууд ярих."
            />
          )}

          {vendor?.email && (
            <DirRow
              label="Имэйл"
              href={`mailto:${vendor.email}`}
              cta="Мэйл илгээх"
              icon={Mail}
              primary={primary}
              headline={vendor.email}
              sub="Ажлын өдөр 24 цагийн дотор хариу өгнө."
            />
          )}

          {vendor?.address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(vendor.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 py-5 border-b border-border"
              style={{ ['--brand' as any]: primary }}
            >
              <div className="h-11 w-11 rounded-lg grid place-items-center shrink-0" style={{ background: `color-mix(in oklch, ${primary} 10%, white)`, color: primary }}>
                <MapPin className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">Хаяг</div>
                <div className="text-base font-medium text-foreground wrap-break-word group-hover:text-(--brand) transition-colors">{vendor.address}</div>
                {vendor?.addressLandmark && (
                  <div className="mt-0.5 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
                    <Navigation className="h-3.5 w-3.5" />
                    {vendor.addressLandmark}
                  </div>
                )}
              </div>
              <div className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
                Газрын зураг
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
